import { Component } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";

/**
 * Catches a render error inside the flow.
 *
 * The app has no error boundary anywhere, so today a throw in any screen
 * unmounts the whole React tree and leaves a white page. That is bad in a
 * dashboard and worse here: onboarding is the first thing a new member ever
 * sees, and a blank screen at that moment is someone who never signs up.
 *
 * Placed around the step outlet rather than the whole flow, so the header,
 * wordmark and progress bar survive the error and the member can still go back
 * to the previous step. Flow state is untouched — it lives in sessionStorage,
 * so retrying or stepping back keeps every answer already given.
 *
 * Still a class: React has no hook equivalent, by design.
 */
class FlowErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Nothing aggregates client errors yet. Logging keeps the stack somewhere a
    // developer can reach it instead of discarding it silently; when error
    // reporting lands, this is the one place to wire it in.
    console.error("Onboarding step failed to render", error, info);
  }

  /**
   * Remounting the subtree is a real fix for a transient failure — a stub that
   * was not ready, a race on first paint — and costs nothing when it is not.
   * `resetKey` changing (the step id) also clears the error, so navigating away
   * is never blocked by a screen that broke.
   */
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Paper
        elevation={0}
        role="alert"
        data-testid="onboarding-error"
        sx={{
          p: 4,
          borderRadius: onboardingTokens.radius,
          backgroundColor: onboardingTokens.card,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t("onboarding.error.heading")}
        </Typography>

        <Typography sx={{ mb: 3, color: onboardingTokens.mutedForeground }}>
          {t("onboarding.error.body")}
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => this.setState({ error: null })}
          >
            {t("onboarding.error.retry")}
          </Button>
        </Box>
      </Paper>
    );
  }
}

export default FlowErrorBoundary;
